import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaMoneyBillWave, FaWallet, FaArrowDown, FaHourglassHalf, FaCheckCircle, 
  FaTimesCircle, FaPlus, FaUniversity, FaIdCard, FaBarcode, FaHandHoldingUsd
} from "react-icons/fa";
import { UserContext } from "../../context/UserContext";
import "../../styles/circuitoDesignSystem.css";

export default function CircuitoFinanceiroPainel() {
  const { circuito } = useOutletContext();
  const { token: tokenCtx } = useContext(UserContext);
  const getToken = () => tokenCtx || localStorage.getItem("token");

  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [loadingSaque, setLoadingSaque] = useState(false);

  const [form, setForm] = useState({
    valor: "", titular: "", cpfTitular: "", tipoChave: "cpf", chavePix: ""
  });

  useEffect(() => {
    if (circuito?._id) buscarFinanceiro();
  }, [circuito]);

  const buscarFinanceiro = async () => {
    try {
      const token = getToken();
      const res = await axios.get(`https://api.ticketdovaqueiro.com/api/circuitos/${circuito._id}/financeiro`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDados(res.data);
    } catch (err) {
      console.error("Erro ao buscar financeiro:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSolicitarSaque = async (e) => {
    e.preventDefault();
    const valorNum = Number(form.valor.replace(",", "."));
    
    if (valorNum > dados?.disponivel) {
      return alert("O valor solicitado ultrapassa o saldo disponível!");
    }

    setLoadingSaque(true);
    try {
      const token = getToken();
      await axios.post(`https://api.ticketdovaqueiro.com/api/circuitos/${circuito._id}/financeiro/saque`, {
        valor: valorNum,
        titular: form.titular,
        cpfTitular: form.cpfTitular,
        tipoChave: form.tipoChave,
        chavePix: form.chavePix
      }, { headers: { Authorization: `Bearer ${token}` } });

      alert("Saque solicitado com sucesso! Nossa equipe analisará o pedido.");
      setModalOpen(false);
      setForm({ valor: "", titular: "", cpfTitular: "", tipoChave: "cpf", chavePix: "" });
      buscarFinanceiro(); // Atualiza a tela
    } catch (err) {
      alert(err.response?.data?.error || "Erro ao solicitar saque.");
    } finally {
      setLoadingSaque(false);
    }
  };

  if (loading) return <div style={{ padding: "40px", color: "#fff", textAlign: "center" }}>Carregando caixa...</div>;

  const fC = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  const composicao = dados?.composicao || {
    vaqueiros: { qtd: 0, valor: 0 },
    cavalosVinculados: { qtd: 0, valor: 0 },
    cavalosAvulsos: { qtd: 0, valor: 0 },
  };
  const categoriasFinanceiras = dados?.porCategoria || [];

  return (
    <div style={{ paddingBottom: "40px" }}>
      <div className="pc-page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
        <div>
          <h1 className="pc-page-header__title">Gestão de Caixa</h1>
          <p className="pc-page-header__subtitle">Acompanhe seus rendimentos e solicite transferências.</p>
        </div>
        <button 
          onClick={() => setModalOpen(true)}
          disabled={dados?.disponivel <= 0}
          className="c-btn c-btn--primary"
          style={{ height: 48, fontSize: 16, display: "flex", gap: 8, alignItems: "center", opacity: dados?.disponivel <= 0 ? 0.5 : 1 }}
        >
          <FaPlus /> Solicitar Retirada
        </button>
      </div>

      {/* CARDS FINANCEIROS (ESTILO BANCO) */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginBottom: 32 }}>
        {/* Card Principal: Disponível */}
        <div style={{ background: "linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)", border: "1px solid rgba(16, 185, 129, 0.3)", padding: 24, borderRadius: 16, position: "relative", overflow: "hidden" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <span style={{ color: "#10b981", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>Disponível para Saque</span>
            <FaWallet size={20} color="#10b981" />
          </div>
          <h2 style={{ fontSize: 36, color: "#fff", margin: 0, fontWeight: 800 }}>{fC(dados?.disponivel)}</h2>
        </div>

        <div className="pc-card" style={{ padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>Arrecadação Total</span>
            <FaMoneyBillWave size={20} color="var(--c-accent)" />
          </div>
          <h2 style={{ fontSize: 24, color: "#fff", margin: 0 }}>{fC(dados?.totalArrecadado)}</h2>
          <small style={{ color: "rgba(255,255,255,.45)" }}>Vendas + entradas manuais</small>
        </div>

        <div className="pc-card" style={{ padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>Receita de Vendas</span>
            <FaMoneyBillWave size={20} color="#38bdf8" />
          </div>
          <h2 style={{ fontSize: 24, color: "#fff", margin: 0 }}>{fC(dados?.totalVendas)}</h2>
        </div>

        <div className="pc-card" style={{ padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>Entradas Manuais</span>
            <FaHandHoldingUsd size={20} color="#10b981" />
          </div>
          <h2 style={{ fontSize: 24, color: "#fff", margin: 0 }}>{fC(dados?.totalEntradas)}</h2>
        </div>

        <div className="pc-card" style={{ padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>Saques Aprovados</span>
            <FaCheckCircle size={20} color="#3b82f6" />
          </div>
          <h2 style={{ fontSize: 24, color: "#fff", margin: 0 }}>{fC(dados?.saquesAprovados)}</h2>
        </div>

        <div className="pc-card" style={{ padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>Saques Pendentes</span>
            <FaHourglassHalf size={20} color="#f59e0b" />
          </div>
          <h2 style={{ fontSize: 24, color: "#fff", margin: 0 }}>{fC(dados?.saquesPendentes)}</h2>
        </div>
      </div>

      {/* COMPOSIÇÃO DA ARRECADAÇÃO — NÃO MISTURA AS DUAS TAXAS DE CAVALO */}
      <div className="pc-card" style={{ marginBottom: 24 }}>
        <div className="pc-card__header" style={{ marginBottom: 18 }}>
          <div>
            <h3 className="pc-card__title">De onde veio a arrecadação</h3>
            <p style={{ margin: "5px 0 0", color: "rgba(255,255,255,0.55)", fontSize: 13 }}>
              O cavalo avulso possui taxa própria e não é somado ao cavalo vinculado ao vaqueiro.
            </p>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12, marginBottom: 20 }}>
          {[
            { label: "Associações", qtd: composicao.vaqueiros.qtd, valor: composicao.vaqueiros.valor, cor: "#38bdf8" },
            { label: "Cavalos c/ vaqueiro", qtd: composicao.cavalosVinculados.qtd, valor: composicao.cavalosVinculados.valor, cor: "#a855f7" },
            { label: "Cavalos avulsos", qtd: composicao.cavalosAvulsos.qtd, valor: composicao.cavalosAvulsos.valor, cor: "#f59e0b" },
          ].map((item) => (
            <div key={item.label} style={{ padding: 16, borderRadius: 12, background: `${item.cor}12`, border: `1px solid ${item.cor}40` }}>
              <span style={{ display: "block", color: item.cor, fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 8 }}>{item.label}</span>
              <strong style={{ color: "#fff", fontSize: 20, display: "block" }}>{fC(item.valor)}</strong>
              <small style={{ color: "rgba(255,255,255,.6)" }}>{item.qtd} cadastro(s)</small>
            </div>
          ))}
        </div>

        <div style={{ overflowX: "auto" }}>
          <table className="pc-table" style={{ minWidth: 860 }}>
            <thead>
              <tr>
                <th>Categoria</th>
                <th>Associações</th>
                <th>Cavalos c/ vaqueiro</th>
                <th>Cavalos avulsos</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {categoriasFinanceiras.length === 0 ? (
                <tr><td colSpan="5" style={{ textAlign: "center", padding: 24, color: "rgba(255,255,255,.5)" }}>Ainda não há receita paga para detalhar.</td></tr>
              ) : categoriasFinanceiras.map((categoria) => (
                <tr key={categoria.nome}>
                  <td style={{ fontWeight: 700, color: "#fff" }}>{categoria.nome}</td>
                  <td>{categoria.vaqueiros.qtd} • {fC(categoria.vaqueiros.valor)}</td>
                  <td>{categoria.cavalosVinculados.qtd} • {fC(categoria.cavalosVinculados.valor)}</td>
                  <td>{categoria.cavalosAvulsos.qtd} • {fC(categoria.cavalosAvulsos.valor)}</td>
                  <td style={{ fontWeight: 800, color: "#10b981" }}>{fC(categoria.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ENTRADAS MANUAIS LANÇADAS PELO ADMIN */}
      <div className="pc-card" style={{ marginBottom: 24 }}>
        <div className="pc-card__header">
          <div>
            <h3 className="pc-card__title">Entradas adicionais</h3>
            <p style={{ margin: "5px 0 0", color: "rgba(255,255,255,0.55)", fontSize: 13 }}>
              Patrocínios, aportes e outros recebimentos lançados pela administração.
            </p>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table className="pc-table" style={{ minWidth: 760 }}>
            <thead>
              <tr>
                <th>Data</th>
                <th>Tipo</th>
                <th>Descrição</th>
                <th>Valor</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {!dados?.entradas?.length ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", padding: 24, color: "rgba(255,255,255,.5)" }}>
                    Nenhuma entrada adicional cadastrada.
                  </td>
                </tr>
              ) : (
                dados.entradas.map((entrada) => (
                  <tr key={entrada._id} style={{ opacity: entrada.status === "cancelado" ? 0.5 : 1 }}>
                    <td>{new Date(entrada.dataEntrada || entrada.createdAt).toLocaleDateString("pt-BR")}</td>
                    <td>{entrada.tipo || "Outro"}</td>
                    <td>{entrada.descricao || "—"}</td>
                    <td style={{ fontWeight: 800, color: entrada.status === "cancelado" ? "#888" : "#10b981", textDecoration: entrada.status === "cancelado" ? "line-through" : "none" }}>
                      {fC(entrada.valor)}
                    </td>
                    <td>
                      <span style={{
                        padding: "6px 12px",
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 800,
                        textTransform: "uppercase",
                        background: entrada.status === "ativo" ? "rgba(16, 185, 129, 0.15)" : "rgba(239, 68, 68, 0.15)",
                        color: entrada.status === "ativo" ? "#10b981" : "#ef4444",
                      }}>
                        {entrada.status === "ativo" ? "Ativa" : "Cancelada"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* TABELA DE HISTÓRICO */}
      <div className="pc-card">
        <div className="pc-card__header">
          <h3 className="pc-card__title">Histórico de Retiradas</h3>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table className="pc-table" style={{ minWidth: 1000 }}>
            <thead>
              <tr>
                <th>Data</th>
                <th>Solicitante / Contato</th>
                <th>Titular da Conta</th>
                <th>CPF Bancário</th>
                <th>Chave PIX</th>
                <th>Valor</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {dados?.historico?.length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign: "center", padding: 30, color: "rgba(255,255,255,0.4)" }}>Nenhum saque solicitado ainda.</td></tr>
              ) : (
                dados?.historico?.map(s => (
                  <tr key={s._id}>
                    <td>{new Date(s.dataSolicitacao).toLocaleDateString('pt-BR')}</td>
                    <td>
                      <strong>{s.donoId?.nomeCompleto?.split(" ")[0]}</strong><br/>
                      <small style={{ color: "rgba(255,255,255,0.5)" }}>{s.donoId?.whatsapp || "S/ Número"}</small>
                    </td>
                    <td>{s.titular || "Não inf."}</td>
                    <td>{s.cpfTitular || "Não inf."}</td>
                    <td>
                      <span style={{ fontSize: 10, background: "rgba(255,255,255,0.1)", padding: "2px 6px", borderRadius: 4, textTransform: "uppercase", marginRight: 6 }}>{s.chavePix?.tipo}</span>
                      {s.chavePix?.valor}
                    </td>
                    <td style={{ fontWeight: "bold", color: "#fff" }}>{fC(s.valor)}</td>
                    <td>
                      <span style={{
                        padding: "6px 12px", borderRadius: 20, fontSize: 12, fontWeight: 800, textTransform: "uppercase",
                        background: s.status === "aprovado" ? "rgba(16, 185, 129, 0.15)" : s.status === "pendente" ? "rgba(245, 158, 11, 0.15)" : "rgba(239, 68, 68, 0.15)",
                        color: s.status === "aprovado" ? "#10b981" : s.status === "pendente" ? "#f59e0b" : "#ef4444"
                      }}>
                        {s.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL DE SAQUE */}
      <AnimatePresence>
        {modalOpen && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
              style={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 24, width: "100%", maxWidth: 500, padding: 32, boxShadow: "0 24px 50px rgba(0,0,0,0.5)" }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                <h2 style={{ fontSize: 22, margin: 0, color: "#fff" }}>Solicitar Retirada</h2>
                <button onClick={() => setModalOpen(false)} style={{ background: "transparent", border: "none", color: "#fff", fontSize: 20, cursor: "pointer" }}>&times;</button>
              </div>

              <div style={{ background: "rgba(16, 185, 129, 0.1)", border: "1px solid rgba(16, 185, 129, 0.3)", borderRadius: 12, padding: 16, marginBottom: 24 }}>
                <span style={{ color: "#10b981", fontSize: 12, fontWeight: "bold", textTransform: "uppercase" }}>Saldo Disponível</span>
                <div style={{ fontSize: 24, fontWeight: 800, color: "#fff" }}>{fC(dados?.disponivel)}</div>
              </div>

              <form onSubmit={handleSolicitarSaque} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <label style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><FaUniversity /> Titular da Conta</label>
                    <input type="text" required value={form.titular} onChange={e=>setForm({...form, titular: e.target.value})} className="c-input" placeholder="Nome Completo" style={{ width: "100%" }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><FaIdCard /> CPF do Titular</label>
                    <input type="text" required value={form.cpfTitular} onChange={e=>setForm({...form, cpfTitular: e.target.value})} className="c-input" placeholder="Apenas números" style={{ width: "100%" }} />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16 }}>
                  <div>
                    <label style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><FaBarcode /> Tipo PIX</label>
                    <select value={form.tipoChave} onChange={e=>setForm({...form, tipoChave: e.target.value})} className="c-input" style={{ width: "100%" }}>
                      <option value="cpf">CPF</option>
                      <option value="cnpj">CNPJ</option>
                      <option value="telefone">Celular</option>
                      <option value="email">E-mail</option>
                      <option value="aleatoria">Chave Aleatória</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", marginBottom: 6 }}>Chave PIX</label>
                    <input type="text" required value={form.chavePix} onChange={e=>setForm({...form, chavePix: e.target.value})} className="c-input" placeholder="Digite a chave" style={{ width: "100%" }} />
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><FaArrowDown /> Valor da Retirada (R$)</label>
                  <input type="number" step="0.01" max={dados?.disponivel} required value={form.valor} onChange={e=>setForm({...form, valor: e.target.value})} className="c-input" placeholder="Ex: 500.00" style={{ width: "100%", fontSize: 18, fontWeight: "bold", color: "#10b981" }} />
                </div>

                <button type="submit" disabled={loadingSaque} className="c-btn c-btn--primary" style={{ height: 50, fontSize: 16, fontWeight: 800, marginTop: 8 }}>
                  {loadingSaque ? "Processando..." : "Confirmar Retirada"}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}